/**
 * rendi-trust-panel — the wizard's trust step (estimate card + answers recap).
 *
 * Dumb like the other wizard components: the host computes every display value
 * and drives the panel through setData(); the panel owns only its collapse state
 * and the DOM painting. The "Ricomincia" button emits `rendi:restart` for the
 * host to wipe the answers.
 */
if (!customElements.get('rendi-trust-panel')) {
  class RendiTrustPanel extends HTMLElement {
    constructor() {
      super();
      this.expanded = false;
      this.handleToggleBound = this.handleToggle.bind(this);
      this.handleRestartBound = this.handleRestart.bind(this);
    }

    connectedCallback() {
      this.estimateNode = this.querySelector('[data-trust-estimate]');
      this.breakdown = this.querySelector('[data-trust-breakdown]');
      this.sellLine = this.querySelector('[data-trust-sell-line]');
      this.returnsLine = this.querySelector('[data-trust-returns-line]');
      this.sellNode = this.querySelector('[data-trust-sell]');
      this.returnsNode = this.querySelector('[data-trust-returns]');
      this.recapEl = this.querySelector('[data-recap]');
      this.recapRows = Array.from(this.querySelectorAll('[data-recap-row]'));
      this.toggle = this.querySelector('[data-recap-toggle]');
      this.toggleMore = this.querySelector('[data-recap-toggle-more]');
      this.toggleLess = this.querySelector('[data-recap-toggle-less]');
      this.restartButton = this.querySelector('[data-wizard-restart]');

      this.toggle?.addEventListener('click', this.handleToggleBound);
      this.restartButton?.addEventListener('click', this.handleRestartBound);
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.handleToggleBound);
      this.restartButton?.removeEventListener('click', this.handleRestartBound);
    }

    handleToggle() {
      this.expanded = !this.expanded;
      this.renderRecap();
    }

    handleRestart() {
      this.dispatchEvent(new CustomEvent('rendi:restart', { bubbles: true }));
    }

    /* ── Host → panel ── */

    // total/sell/returns are already-formatted strings (the host owns the
    // calculation and locale formatting); `rows` is a { recapKey: label } map,
    // an empty string for rows that shouldn't show.
    setData({ total, sell, returns, showBreakdown, rows }) {
      if (this.estimateNode) this.estimateNode.textContent = total;
      if (this.breakdown) this.breakdown.hidden = !showBreakdown;
      if (this.sellLine) this.sellLine.hidden = !showBreakdown;
      if (this.returnsLine) this.returnsLine.hidden = !showBreakdown;
      if (this.sellNode) this.sellNode.textContent = sell;
      if (this.returnsNode) this.returnsNode.textContent = returns;
      this.rows = rows || {};
      this.renderRecap();
    }

    // A row with no value stays hidden; collapsed mode shows only the first two
    // rows with a value.
    renderRecap() {
      if (!this.recapEl) return;
      const rows = this.rows || {};
      let filledCount = 0;
      this.recapRows.forEach((row) => {
        const value = rows[row.dataset.recapRow] || '';
        const valueNode = row.querySelector('[data-recap-value]');
        if (valueNode) valueNode.textContent = value;
        const hasValue = Boolean(value);
        row.hidden = !hasValue || (filledCount >= 2 && !this.expanded);
        if (hasValue) filledCount += 1;
      });

      this.recapEl.classList.toggle('is-expanded', this.expanded);
      if (this.toggle) this.toggle.hidden = filledCount <= 2;
      if (this.toggleMore) this.toggleMore.hidden = this.expanded;
      if (this.toggleLess) this.toggleLess.hidden = !this.expanded;
    }

    // "Ricomincia" collapses the recap back to its two-row default.
    reset() {
      this.expanded = false;
    }
  }

  customElements.define('rendi-trust-panel', RendiTrustPanel);
}
